import moment from "moment";
import * as yup from "yup";

import { STANDARD_DOB_MATCHES_VALIDATION } from "@/components/Common/FormHelpers/MemberInfo/ValidatorBuilder";

import {
  ACTIVITY_PURPOSE_OPTIONS,
  ACTIVITY_REASON_OPTIONS,
  ACTIVITY_SEVICE_TYPE_OPTIONS,
  CARE_TEAM_MEMBER_TYPE_OPTIONS,
  COMMUNICATION_TYPE_OPTIONS,
  REASON_FOR_COMMUNICATION_OPTIONS,
  RECOMMENDED_NEXT_OPTIONS,
  TIME_SLOT_OPTIONS,
  YES_NO_OPTIONS,
} from "./constants";

export const ADVANCED_CARE_ACTIVITY_SCHEMA = yup.object().shape({
  content: yup.object().shape({
    // This is converted to a boolean on submission
    active_substance: yup
      .string()
      .required("This is a required field")
      .oneOf(
        YES_NO_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    active_substance_details: yup.string().when("active_substance", {
      is: (active_substance) => active_substance === "Yes",
      then: yup
        .string()
        .required("This is a required field")
        .max(1000, "Content must not exceed 1000 characters"),
    }),

    // This is converted to a boolean on submission
    activity_difficulties: yup
      .string()
      .required("This is a required field")
      .oneOf(
        YES_NO_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    activity_difficulties_details: yup.string().when("activity_difficulties", {
      is: (activity_difficulties) => activity_difficulties === "Yes",
      then: yup
        .string()
        .required("This is a required field")
        .max(1000, "Content must not exceed 1000 characters"),
    }),

    activity_purpose: yup
      .array()
      .required("This is a required field")
      .test("activity_purpose", "An invalid answer was selected", (value) => {
        const activity_purpose_values = ACTIVITY_PURPOSE_OPTIONS.map((item) => item.value);

        return value.every((item) => activity_purpose_values.includes(item));
      }),

    activity_purpose_other_details: yup.string().when("activity_purpose", {
      is: (activity_purpose) => activity_purpose.includes("Other"),
      then: yup
        .string()
        .required("This is a required field")
        .max(1000, "Content must not exceed 1000 characters"),
    }),

    activity_reason: yup
      .string()
      .required("This is a required field")
      .oneOf(
        ACTIVITY_REASON_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    // This is converted to a boolean on submission
    activity_result: yup
      .string()
      .required("This is a required field")
      .oneOf(
        YES_NO_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    activity_result_note: yup.string().when("activity_result", {
      is: (activity_result) => activity_result === "No",
      then: yup
        .string()
        .required("This is a required field")
        .max(1000, "Content must not exceed 1000 characters"),
    }),

    activity_service_type: yup
      .string()
      .required("This is a required field")
      .oneOf(
        ACTIVITY_SEVICE_TYPE_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    // This is converted to a boolean on submission
    acute_episode: yup
      .string()
      .required("This is a required field")
      .oneOf(
        YES_NO_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    acute_episode_details: yup.string().when("acute_episode", {
      is: (acute_episode) => acute_episode === "Yes",
      then: yup
        .string()
        .required("This is a required field")
        .max(1000, "Content must not exceed 1000 characters"),
    }),

    care_team_member_type: yup
      .string()
      .required("This is a required field")
      .oneOf(
        CARE_TEAM_MEMBER_TYPE_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    care_team_member_type_other_details: yup.string().when("care_team_member_type", {
      is: (care_team_member_type) => care_team_member_type === "Other",
      then: yup.string().required("This is a required field"),
    }),

    communication_type: yup
      .string()
      .required("This is a required field")
      .oneOf(
        COMMUNICATION_TYPE_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    communication_type_other_details: yup.string().when("communication_type", {
      is: (communication_type) => communication_type === "Other",
      then: yup
        .string()
        .required("This is a required field")
        .max(1000, "Content must not exceed 1000 characters"),
    }),

    intended_duration: yup
      .string()
      .required("This is a required field")
      .test("intended_duration", "Please enter a duration in 5 minute increments", (value) => {
        const valueAsNumber = Number(value);

        if (!valueAsNumber) {
          return false;
        }

        return valueAsNumber % 5 === 0;
      })
      .test("intended_duration", "Duration cannot be longer than a day", (value) => {
        const valueAsNumber = Number(value);

        if (!valueAsNumber) {
          return false;
        }

        return valueAsNumber <= 1440;
      }),

    // This is converted to a boolean on submission
    patient_encounter: yup
      .string()
      .required("This is a required field")
      .oneOf(
        YES_NO_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    // This is converted to a boolean on submission
    patient_participant_attest: yup
      .string()
      .required("This is a required field")
      .oneOf(
        YES_NO_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    reason_for_communication: yup
      .array()
      .required("This is a required field")
      .test("reason_for_communication", "An invalid answer was selected", (value) => {
        const reason_for_communication_values = REASON_FOR_COMMUNICATION_OPTIONS.map(
          (item) => item.value,
        );

        return value.every((item) => reason_for_communication_values.includes(item));
      }),

    recommended_next: yup
      .array()
      .required("This is a required field")
      .test("recommended_next", "An invalid answer was selected", (value) => {
        const recommended_next_values = RECOMMENDED_NEXT_OPTIONS.map((item) => item.value);

        return value.every((item) => recommended_next_values.includes(item));
      }),

    recommended_next_other_details: yup.string().when("recommended_next", {
      is: (recommended_next) => recommended_next.includes("Other"),
      then: yup.string().required("This is a required field"),
    }),

    // This is converted to a boolean on submission
    sdoh: yup
      .string()
      .required("This is a required field")
      .oneOf(
        YES_NO_OPTIONS.map((item) => item.value),
        "An invalid answer was selected",
      ),

    sdoh_details: yup.string().when("sdoh", {
      is: (sdoh) => sdoh === "Yes",
      then: yup
        .string()
        .required("This is a required field")
        .max(1000, "Content must not exceed 1000 characters"),
    }),
  }),

  end_date_time_error: yup
    .string()
    .when(["session_to", "session_date"], (session_to, session_date) => {
      const endTime = moment(`${session_date} ${session_to}`, "MMDDYYYY HH:mm");
      const today = moment();

      if (endTime.isAfter(today)) {
        return yup
          .string()
          .test("date_time_error", "The end time must be in the past", () => false);
      }

      return yup.string();
    }),

  session_date: STANDARD_DOB_MATCHES_VALIDATION()
    .required("This is a required field")
    .test("activity_purpose", "Activity Date must be in the past", (value) => {
      const today = moment();
      const enteredDay = moment(value, "MMDDYYYY");

      // error if the entered day is in the future
      return today.isAfter(enteredDay);
    }),

  session_from: yup
    .string()
    .required("This is a required field")
    .oneOf(
      TIME_SLOT_OPTIONS.map((item) => item.value),
      "An invalid answer was selected",
    ),

  session_to: yup
    .string()
    .required("This is a required field")
    .oneOf(
      TIME_SLOT_OPTIONS.map((item) => item.value),
      "An invalid answer was selected",
    ),

  signature: yup.string().required("This is a required field"),

  start_date_time_error: yup
    .string()
    .when(["session_from", "session_date"], (session_from, session_date) => {
      const startTime = moment(`${session_date} ${session_from}`, "MMDDYYYY HH:mm");
      const today = moment();

      if (startTime.isSameOrAfter(today)) {
        return yup
          .string()
          .test("date_time_error", "The start time must be in the past", () => false);
      }

      return yup.string();
    }),

  time_error: yup.string().when(["session_from", "session_to"], (session_from, session_to) => {
    if (!session_from || !session_to) {
      return yup.string();
    }

    const allottedTime = moment(session_to, "HH:mm").diff(moment(session_from, "HH:mm"), "minutes");

    if (allottedTime <= 0) {
      return yup
        .string()
        .test("time_error", "Please enter a valid start and end time", () => false);
    }

    return yup.string();
  }),
});
